import {styled} from '@linaria/react';
import React from 'react';
import {useGetAriaLabelFromUrl} from '../../../hooks/useGetAriaLabelFromUrl/useGetAriaLabelFromUrl';
import {ImageType} from '../../../types/Image';
import {UnstyledLinkLinaria} from '../../atoms/UnstyledLink/UnstyledLinkLinaria';
import {FullWidthImage} from '../../molecules/FullWidthImage/FullWidthImage';
import {useFullWidthSmallImage} from '../../molecules/FullWidthImage/useFullWidthSmallImage';

interface CardAmbientAmbient {
  className?: string;
  ['data-testid']: string;
  isCloseToViewport: boolean;
  isActive: boolean;
  image: ImageType;
  onClick?: VoidFunction;
  href?: string;
}

export const CardAmbient: React.FC<
  React.PropsWithChildren<CardAmbientAmbient>
> = ({
  className,
  'data-testid': dataTestId,
  isActive,
  isCloseToViewport,
  image,
  onClick,
  href,
  children,
}) => {
  const getAriaLabelFromUrl = useGetAriaLabelFromUrl();
  const backgroundImageUrl = useFullWidthSmallImage({
    ratio: '1:1',
    src: image.url,
  });

  return (
    <CardContainer
      className={className}
      data-testid={dataTestId}
      data-background-image-url={backgroundImageUrl}
      onClick={onClick}
    >
      <CardContainerBackground $backgroundImageUrl={backgroundImageUrl} />
      <Card data-active={isActive}>
        <StyledFullWidthImage
          dataTestId="browseCardImage"
          imageUrl={image.url}
          description={image.description}
          aspectRatio="2:1"
          highPrioImage={isCloseToViewport}
          disableFixedRatio
          imagePosition="top"
        />
        {href && (
          <BannerTarget
            to={href}
            data-testid="bannerTarget"
            aria-label={getAriaLabelFromUrl(href)}
          />
        )}
        {children}
      </Card>
    </CardContainer>
  );
};

const CardContainer = styled.div`
  height: 100%;
  position: relative;
`;

const CardContainerBackground = styled.div<{$backgroundImageUrl: string}>`
  backface-visibility: hidden;
  perspective: 1000;
  transform: translate3d(0, 0, 0);
  transform: translateZ(0);

  content: '';
  position: absolute;
  inset: -6rem;
  border-radius: 0.5rem;
  background: ${props => `linear-gradient(
      0deg,
      rgba(0, 0, 0, 0.8) 0%,
      rgba(0, 0, 0, 0.8) 100%
    ),
    url(${props.$backgroundImageUrl}), lightgray 50% / cover no-repeat`};
  background-blend-mode: overlay, normal;
  background-size: contain;
  filter: blur(75px);
`;

const Card = styled.div`
  position: relative;
  height: 100%;
  border-radius: 8px;
  overflow: hidden;
  z-index: 1;
  aspect-ratio: 1;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0.02) 0%,
      rgba(0, 0, 0, 0.04) 40%,
      rgba(0, 0, 0, 0.2) 50%,
      rgba(0, 0, 0, 0.4) 100%
    );
  }

  @keyframes cover-flow {
    0%,
    100% {
      transform: scaleY(0.85);
      opacity: 0.4;
    }
    50% {
      transform: scaleY(1);
      opacity: 1;
    }
  }

  @supports (view-timeline-axis: inline) {
    --timeline-name: --cover;
    view-timeline: var(--timeline-name);
    will-change: transform, opacity;
    view-timeline-axis: inline;
    animation: cover-flow both linear;
    animation-timeline: var(--timeline-name);
  }

  @supports not (view-timeline-axis: inline) {
    transform: scaleY(0.85);
    opacity: 0.4;
    transition: transform 0.3s ease-out, opacity 0.3s ease-out;
    will-change: transform;

    &[data-active='true'] {
      transform: scaleY(1);
      opacity: 1;

      > * {
        transition: transform 0.3s ease-out, opacity 0.3s ease-out;
        transform: scaleY(1);
        opacity: 1;
      }
    }

    &[data-active='false'] {
      transform: scaleY(0.85);
      opacity: 0.4;

      > * {
        transition: transform 0.3s ease-out, opacity 0.3s ease-out;
        transform: scaleY(1.15);
        opacity: 1;
      }
    }
  }
`;

const StyledFullWidthImage = styled(FullWidthImage)`
  height: 100%;
`;

export const ContentContainer = styled.div<{$isCampaignCard?: boolean}>`
  position: absolute;
  inset: ${props => (props.$isCampaignCard ? 0 : 'unset')};
  bottom: 28px;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  z-index: 1;
  padding: 0 16px;
  pointer-events: none;
`;

const BannerTarget = styled(UnstyledLinkLinaria)`
  position: absolute;
  inset: 0;
  z-index: 1;
`;
